<script setup>
import { ref, onMounted, computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'vuex';
import axios from 'axios';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import SectionLayout from './SectionLayout.vue';

const store = useStore();
const { currentAccount } = useAccount();
const currentUser = computed(() => store.getters.getCurrentUser);
const accessToken = computed(() => currentUser.value?.access_token || '');

// Aceleres — Fase 1: team-scoped. Quando teamId vem (modal do time), envia
// X-Chatwoot-Team-Id pro Laravel; ausente (página global) = account-level.
const props = defineProps({
  teamId: { type: [Number, String], default: null },
});

const LARAVEL_URL = `${resolveAceleresApiBase()}/api/aurora/nome-ia`;

const nome   = ref('');
const locked = ref(false);
const loading = ref(true);
const saving  = ref(false);
const savedMsg = ref('');
const errorMsg = ref('');

function headers() {
  return {
    'X-Chatwoot-Token':      accessToken.value,
    'X-Chatwoot-Account-Id': currentAccount.value?.id || '',
    ...(props.teamId ? { 'X-Chatwoot-Team-Id': String(props.teamId) } : {}),
  };
}

async function carregar() {
  loading.value  = true;
  errorMsg.value = '';
  try {
    const resp = await axios.get(LARAVEL_URL, { headers: headers() });
    nome.value   = resp.data.nome_ia  || '';
    locked.value = !!resp.data.locked;
  } catch {
    // Oculta silenciosamente se a conta não tiver config
    nome.value = '';
  } finally {
    loading.value = false;
  }
}

async function salvar() {
  if (locked.value || saving.value) return;
  const valor = nome.value.trim();
  if (!valor) { errorMsg.value = 'Informe um nome.'; return; }

  saving.value   = true;
  savedMsg.value = '';
  errorMsg.value = '';
  try {
    const resp = await axios.patch(
      LARAVEL_URL,
      { nome_ia: valor },
      { headers: headers() }
    );
    nome.value     = resp.data.nome_ia || valor;
    savedMsg.value = 'Salvo!';
    setTimeout(() => { savedMsg.value = ''; }, 3000);
  } catch {
    errorMsg.value = 'Erro ao salvar. Tente novamente.';
  } finally {
    saving.value = false;
  }
}

onMounted(carregar);
</script>

<template>
  <SectionLayout
    v-if="!loading"
    title="Nome da IA"
    description="Nome pelo qual a IA se apresenta nos atendimentos desta empresa."
    with-border
  >
    <div class="flex flex-col gap-3 mt-2">
      <div v-if="locked" class="text-sm text-slate-500">
        O nome da IA está fixo para esta empresa.
      </div>
      <div v-else class="flex items-center gap-3 flex-wrap">
        <input
          v-model="nome"
          type="text"
          maxlength="100"
          placeholder="Ex.: Sofia, Atena, IA..."
          class="rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 w-64"
          :disabled="saving"
          @keyup.enter="salvar"
        />
        <button
          type="button"
          :disabled="saving"
          class="rounded-md bg-woot-500 px-4 py-2 text-sm font-medium text-white hover:bg-woot-600 disabled:opacity-50 cursor-pointer"
          @click="salvar"
        >
          {{ saving ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
      <span v-if="savedMsg" class="text-sm text-green-600 font-medium">{{ savedMsg }}</span>
      <span v-if="errorMsg" class="text-sm text-red-600 text-xs">{{ errorMsg }}</span>
    </div>
  </SectionLayout>
</template>
